import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { SUBSCRIPTION_STATUS } from '@shared/constants/subscriptionStatus';
import type { UserResponse } from '@shared/schemas';

import { initialState } from './users.state';

/**
 * Users index slice. Reducers are pure state transitions only; the fetch, the
 * delete DELETE and the archive PATCH live in the saga, which dispatches these `load*` / `delete*`
 * transitions as it progresses. `invitationsSent` is a plain transition (no side
 * effect): the `InvitationDialog` performs the bulk-send itself, then the page
 * dispatches this to reflect the newly-pending rows.
 */
const slice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    loadStarted: (state) => {
      state.loading = true;
      state.error = null;
    },
    loadSucceeded: (state, action: PayloadAction<UserResponse[]>) => {
      state.rows = action.payload;
      state.loading = false;
    },
    loadFailed: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    // ── Delete lifecycle ──
    deleteStarted: (state) => {
      state.deleteStatus = 'saving';
    },
    /** Drop the deleted row on success (optimistic removal in the original page). */
    deleteSucceeded: (state, action: PayloadAction<string>) => {
      state.deleteStatus = 'succeeded';
      state.rows = state.rows.filter((u) => u.id !== action.payload);
    },
    deleteFailed: (state, action: PayloadAction<string>) => {
      state.deleteStatus = 'failed';
      state.error = action.payload;
    },
    // ── Deactivate / reactivate lifecycle ──
    archiveStarted: (state) => {
      state.archiveStatus = 'saving';
      state.archiveError = null;
    },
    /** Write the server's `archived_at` on the row, so its chip and the band follow. */
    archiveSucceeded: (state, action: PayloadAction<{ id: string; archivedAt: string | null }>) => {
      state.archiveStatus = 'succeeded';
      const row = state.rows.find((u) => u.id === action.payload.id);
      if (row) row.archived_at = action.payload.archivedAt;
    },
    archiveFailed: (state, action: PayloadAction<string>) => {
      state.archiveStatus = 'failed';
      state.archiveError = action.payload;
    },
    /** The page has told the reader how it went; back to idle for the next one. */
    archiveCleared: (state) => {
      state.archiveStatus = 'idle';
      state.archiveError = null;
    },
    /** Flip the given rows to PENDING after a successful bulk invitation send. */
    invitationsSent: (state, action: PayloadAction<string[]>) => {
      const sent = new Set(action.payload);
      state.rows.forEach((u) => {
        if (sent.has(u.id)) u.subscription_status = SUBSCRIPTION_STATUS.PENDING;
      });
    },
  },
});

export const usersActions = slice.actions;
export const usersReducer = slice.reducer;
